// src/data/worksheets/adaptability.js — performance-side pair (authored).
// Built off the Adaptability action plan: plan B in advance, control the controllables, debrief the surprise.

export const ADAPTABILITY_CHECKLIST = {
    id: 'adaptability_checklist', topicId: 'adaptability', kind: 'checklist',
    title: 'Adaptability Checklist',
    intro: 'Things will not go to plan. The skill is what you do in the thirty seconds after that.',
    sections: [
        { id: 'before', title: 'Before It Changes', prompts: [
            { id: 'planb', type: 'checklist', label: 'Plan B in Advance', items: [
                { id: 'p1', label: 'For the next big thing, decide now what you will do if it changes (venue, time, role, weather, team).' },
                { id: 'p2', label: 'Write the plan B down — a plan in your head disappears under stress.' }
            ] },
            { id: 'expect', type: 'checklist', label: 'Expect the Unexpected', items: [
                { id: 'e1', label: 'List the three most likely surprises for the week ahead.' },
                { id: 'e2', label: 'Pre-decide your first move for each.' }
            ] }
        ] },
        { id: 'during', title: 'When It Changes', prompts: [
            { id: 'controllables', type: 'checklist', label: 'Control the Controllables', items: [
                { id: 'c1', label: 'Split it fast: what can I change, what can\'t I. Two columns.' },
                { id: 'c2', label: 'Put your effort on the first column only — attitude, effort, preparation, next action.' }
            ] },
            { id: 'reset', type: 'checklist', label: 'Reset, Then Respond', items: [
                { id: 'r1', label: 'One breath before you react. The first reaction is rarely the best one.' },
                { id: 'r2', label: 'Ask "what does this situation need?" instead of "why did this happen?"' }
            ] }
        ] },
        { id: 'flex', title: 'Build Flexibility', prompts: [
            { id: 'vary', type: 'checklist', label: 'Vary on Purpose', items: [
                { id: 'v1', label: '{Change one variable in training or work|Change one variable in your work} each week (order, environment, timing, tools).' },
                { id: 'v2', label: 'Practice the skill under a different condition than you\'ll use it.' }
            ] },
            { id: 'mindset', type: 'checklist', label: 'Hold Plans Loosely, Standards Tightly', items: [
                { id: 'm1', label: 'Separate the goal (fixed) from the route (flexible).' },
                { id: 'm2', label: 'When the route changes, restate the goal out loud.' }
            ] }
        ] },
        { id: 'after', title: 'After the Surprise', prompts: [
            { id: 'debrief', type: 'checklist', label: 'Debrief the Surprise', items: [
                { id: 'd1', label: 'Note what you did well and what you\'d do differently next time.' },
                { id: 'd2', label: 'Add the surprise to your "likely surprises" list so it never catches you twice.' }
            ] },
            { id: 'people', type: 'checklist', label: 'Learn From Others', items: [
                { id: 'pp1', label: 'Ask someone who handled a change well what they were thinking at the time.' },
                { id: 'pp2', label: 'Borrow one of their moves.' }
            ] }
        ] },
        { id: 'review', title: 'Review & Reflect', prompts: [
            { id: 'weekly', type: 'checklist', label: 'Weekly', items: [
                { id: 'wk1', label: 'Rate how you handled this week\'s changes.' },
                { id: 'wk2', label: 'Update the plan B for next week\'s big thing.' }
            ] },
            { id: 'notes', type: 'text', label: 'Notes', multiline: true }
        ] }
    ],
    outro: 'Adaptable people are not calmer by nature. They have decided their first move before they need it.'
};

export const ADAPTABILITY_WORKSHEET = {
    id: 'adaptability_worksheet', topicId: 'adaptability', kind: 'worksheet',
    title: 'Adaptability Worksheet',
    intro: 'Answer in your own words. Nothing here is scored, and only you can see it.',
    sections: [
        { id: 'self', title: 'Self Assessment', prompts: [
            { id: 'level', type: 'scale', label: 'How well do you handle things not going to plan?', help: 'Think about the last few weeks.', min: 1, max: 10, minLabel: 'Thrown off completely', maxLabel: 'Adjust and go' },
            { id: 'changes', type: 'list', label: 'Recent changes that threw you', help: 'Schedule, role, environment, people, plans.', count: 3 },
            { id: 'reactions', type: 'list', label: 'How you tend to react to change', help: 'e.g., freeze, get frustrated, over-plan, push harder on the old plan.', count: 3 }
        ] },
        { id: 'understanding', title: 'Understanding Your Response', prompts: [
            { id: 'best', type: 'text', label: 'A change you handled well — what did you do first?', help: 'This is your template.', multiline: true },
            { id: 'controllables', type: 'list', label: 'In your current challenge: what you CAN control', count: 3 },
            { id: 'uncontrollables', type: 'list', label: '…and what you cannot', help: 'Name them so you can stop spending effort there.', count: 3 }
        ] },
        { id: 'building', title: 'Building Adaptability', prompts: [
            { id: 'bigthing', type: 'text', label: 'The next big thing coming up' },
            { id: 'surprises', type: 'list', label: 'The three most likely surprises', count: 3 },
            { id: 'planb', type: 'list', label: 'Your first move for each', help: 'Pre-decided, written down.', count: 3, itemLabels: ['If surprise 1', 'If surprise 2', 'If surprise 3'] },
            { id: 'goalroute', type: 'text', label: 'The goal (fixed) vs the route (flexible)', help: 'State the goal in one line so you can hold onto it when the route changes.' }
        ] },
        { id: 'plan', title: 'Action Plan', prompts: [
            { id: 'vary', type: 'text', label: 'One variable you will change on purpose this week' },
            { id: 'resetrule', type: 'text', label: 'Your reset rule when something changes', help: 'e.g., "one breath, two columns, first move".' },
            { id: 'mentor', type: 'text', label: 'Someone who handles change well — what will you ask them?' }
        ] },
        { id: 'track', title: 'Track It', description: 'Log the changes and how composed the response was. The debrief column is where the learning lives.', prompts: [
            { id: 'tracker', type: 'table', label: 'Change log', columns: [
                { id: 'day', label: 'Day', kind: 'date' },
                { id: 'change', label: 'What changed', kind: 'text' },
                { id: 'response', label: 'Response (1–10, 10 = composed)', kind: 'number', min: 1, max: 10 },
                { id: 'controlled', label: 'What I controlled', kind: 'text' },
                { id: 'debrief', label: 'Next time', kind: 'text' }
            ], maxRows: 31, chart: { x: 'day', series: [{ id: 'response', label: 'Response' }] } }
        ] }
    ],
    outro: 'Plans change. Standards don\'t. Decide the first move early and the rest follows.'
};

export const ADAPTABILITY_WORKSHEETS = [ADAPTABILITY_CHECKLIST, ADAPTABILITY_WORKSHEET];
